<script lang="ts" setup>
import InvestorHero from '@/domains/investor/components/Hero/Hero.vue'
import Chart from '@/components/Chart/Chart.vue'
import Table from '@/domains/investor/components/Table/Table.vue'
import { useCharts } from '@/components/Chart/Chart.composables'
import { useFetchShareholders } from '@/domains/investor/composables/useFetchShareholders'
import type { IUiTabsOption } from '@/components/UiTabs/UiTabs.types'
import type { IAdaptChart } from '@/types/shared-types'

const DOUGHNUT_VARIANT = 'doughnut'
const BAR_VARIANT = 'barY'

definePageMeta({
  layout: 'investor',
})

const { t, locale } = useI18n()

useHead(() => ({
  title: t('pageTitles.investorStockShareholders.title'),
  meta: [
    {
      name: 'description',
      content: t('pageTitles.investorStockShareholders.meta'),
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: t(`pageTitles.investorStockShareholders.canonical.${locale.value}`),
    },
    {
      rel: 'alternate',
      hreflang: 'pl',
      href: t('pageTitles.investorStockShareholders.canonical.pl'),
    },
    {
      rel: 'alternate',
      hreflang: 'en',
      href: t('pageTitles.investorStockShareholders.canonical.en'),
    },
    {
      rel: 'alternate',
      hreflang: 'x-default',
      href: t('pageTitles.investorStockShareholders.canonical.pl'),
    },
  ],
}))

const { prepareChartParams } = useCharts()
const selectedChartVariant = ref<string>(DOUGHNUT_VARIANT)
const { fetchShareholders } = await useFetchShareholders({ selectedChartVariant })

const shareholders = await fetchShareholders()

const chartVariants: IUiTabsOption[] = [
  {
    value: DOUGHNUT_VARIANT,
    label: t('investor.stock.shareholders.chartVariants.shareholders'),
  },
  {
    value: BAR_VARIANT,
    label: t('investor.stock.shareholders.chartVariants.significantPackages'),
  },
]

const isDoughnut = computed(() => selectedChartVariant.value === DOUGHNUT_VARIANT)

const structureChartParams = computed(() => {
  const chart = shareholders.data.value?.chart

  if (!chart) {
    return null
  }

  return prepareChartParams({
    ...chart,
    type: selectedChartVariant.value as IAdaptChart['type'],
  })
})

const chartOptions = computed(() => {
  const params = structureChartParams.value

  if (!params) {
    return undefined
  }

  if (!isDoughnut.value) {
    return params.options
  }

  return {
    ...params.options,
    plugins: {
      ...params.options.plugins,
      legend: {
        ...params.options.plugins?.legend,
        display: false,
      },
    },
  }
})

const legendItems = computed(() => {
  const data = structureChartParams.value?.data
  const colors = data?.datasets?.[0]?.backgroundColor
  const values = data?.datasets?.[0]?.data
  const displayValues = shareholders.data.value?.chart?.chartData.map(
    item => item.displayValues?.[0] ?? String(item.values[0] ?? ''),
  ) ?? []
  const numericValues = Array.isArray(values)
    ? values.map(value => typeof value === 'number' ? value : 0)
    : []
  const total = numericValues.reduce((sum, value) => sum + value, 0)

  return (data?.labels ?? []).map((label, index) => ({
    label: String(label),
    value: Array.isArray(values) ? values[index] : undefined,
    displayValue: displayValues[index],
    percentage: total > 0 ? ((numericValues[index] ?? 0) / total) * 100 : undefined,
    color: Array.isArray(colors) ? colors[index] as string : colors as string,
    text: formatLegendLabel(
      String(label),
      Array.isArray(values) ? values[index] : undefined,
      total > 0 ? ((numericValues[index] ?? 0) / total) * 100 : undefined,
      displayValues[index],
    ),
  }))
})

const formatLegendLabel = (label: string, value: unknown, percentage?: number, displayValue?: string) => {
  if (value === undefined && !displayValue) {
    return label
  }

  const formattedPercentage = percentage === undefined ? '' : ` (${percentage.toFixed(1)}%)`
  const formattedValue = displayValue || String(value)

  return `${label}: ${formattedValue}${formattedPercentage}`
}

const shareholdersTableSummary = computed(() => {
  const summary = shareholders.data.value?.tableSummary ?? []

  return summary.length
    ? [t('investor.stock.shareholders.table.total'), ...summary]
    : []
})
</script>

<template>
  <div class="investor-shareholders-page">
    <InvestorHero
      class="investor-shareholders-page__hero"
      :title="$t('investor.stock.shareholders.title')"
      :lead="$t('investor.stock.shareholders.description')"
      image="/images/investor/hero/akcjonarniat.png"
    />

    <UiSection class="investor-shareholders-page__content">
      <div class="section__container">

        <UiHeader
          v-if="structureChartParams"
          :header="structureChartParams.title"
          position="center"
          header-size="h5"
          header-size-tablet="h5"
          header-size-mobile="h7"
        />

        <div class="investor-shareholders-page__summary">
          <p
            v-if="shareholders.data.value?.numberOfShares"
            class="investor-shareholders-page__shares"
          >
            {{ $t('investor.stock.shareholders.totalShares') }}
            <span class="investor-shareholders-page__shares-value">
              {{ shareholders.data.value.numberOfShares }}
            </span>
          </p>

          <UiTabs
            v-model="selectedChartVariant"
            :options="chartVariants"
            :aria-label="$t('investor.stock.shareholders.chartVariants.label')"
          />
        </div>

        <div
          v-if="structureChartParams"
          class="investor-shareholders-page__chart"
          :class="{ 'investor-shareholders-page__chart--doughnut': isDoughnut }"
        >
          <Chart
            :type="structureChartParams.type"
            :data="structureChartParams.data"
            :options="chartOptions"
            height="var(--shareholders-chart-height)"
          />

          <ul
            v-if="isDoughnut"
            class="investor-shareholders-page__legend"
          >
            <li
              v-for="item in legendItems"
              :key="item.label"
              class="investor-shareholders-page__legend-item"
            >
              <span
                class="investor-shareholders-page__legend-dot"
                :style="{ backgroundColor: item.color }"
              />
              {{ item.text }}
            </li>
          </ul>
        </div>

        <p class="investor-shareholders-page__footnote">
          {{ $t('investor.stock.shareholders.footnote') }}
        </p>

        <Table
          v-if="shareholders.data.value?.table"
          :table="shareholders.data.value.table"
          :summary="shareholdersTableSummary"
          :options="{ align: 'left' }"
        />
      </div>
    </UiSection>
  </div>
</template>

<style scoped lang="scss">
.investor-shareholders-page {
  &__content.section {
    display: flex;
    flex-direction: column;
    gap: 3.2rem;
    margin: 0 auto;
  }

  &__title {
    @include h7;
    color: $text-400;
    text-align: center;
  }

  &__summary {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 1.6rem;
    margin-bottom:6.4rem;
  }

  &__summary :deep(.uiTabs__group) {
    flex-direction: column;
    gap: 0.8rem;
  }

  &__shares {
    @include h8;
    color: $text-400;
    text-align: center;
  }

  &__shares-value {
    display: block;
    color: $text-300;
  }

  &__chart {
    --shareholders-chart-height: 40rem;
    margin-bottom: 3.2rem;
  }

  &__chart--doughnut {
    margin-bottom: 6.4rem;
  }

  &__legend {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    width: fit-content;
    margin-top: 2.4rem;
    margin-right: auto;
    margin-left: auto;
  }

  &__legend-item {
    @include body-4;
    display: flex;
    align-items: center;
    gap: 1.2rem;
    color: $text-400;
  }

  &__legend-dot {
    flex-shrink: 0;
    width: 1.6rem;
    height: 1.6rem;
    border-radius: 0.4rem;
  }

  &__footnote {
    @include body-4;
    margin-bottom: 6.4rem;
    color: $text-200;
    text-align: center;
  }

  @include atXS {
    &__content.section {
      gap: 4.8rem;
    }

    &__summary {
      align-items: center;
    }

    &__summary :deep(.uiTabs__group) {
      flex-direction: row;
      gap: 0.4rem;
    }

    &__shares-value {
      display: inline;
    }
  }

  @include atM {
    &__content.section {
      gap: 6.4rem;
    }

    &__title {
      @include h6;
    }

    &__chart--doughnut {
      --shareholders-chart-height: 55rem;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 6.4rem;
    }

    &__chart--doughnut :deep(.chart) {
      flex: 0 1 55rem;
    }

    &__chart--doughnut :deep(.chart__content) {
      max-width: 55rem;
    }
  }
}
</style>
